/**
 * Global media parallax.
 *
 * Two modes:
 *
 * 1. Masked (default): add `data-parallax` to a clipping container — the first
 *    img/video/svg inside drifts vertically behind the container mask. The media
 *    is pre-scaled to 1.15 in CSS and the shift is clamped to the scale slack,
 *    so the container edge is never revealed.
 *
 * 2. Self: add `data-parallax-target="self"` alongside `data-parallax` — the
 *    element ITSELF floats on scroll (no scale, no clipping).
 *
 * Optional on either mode:
 *   data-parallax-speed="0.06"  (drift speed, default 0.06 — higher = faster)
 *
 * Matching CSS lives in base.css under "Media parallax".
 */
(function () {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const state = new Map();
  let running = false;

  function collect(root) {
    (root || document).querySelectorAll('[data-parallax]').forEach(function (container) {
      const self = container.getAttribute('data-parallax-target') === 'self';
      const target = self ? container : container.querySelector('img, video, svg');
      if (!target || state.has(target)) return;
      const speed = parseFloat(container.getAttribute('data-parallax-speed')) || 0.06;
      state.set(target, { container: container, current: 0, target: 0, speed: speed, self: self });
    });
  }

  function onScroll() {
    state.forEach(function (s, el) {
      if (!document.contains(el)) {
        state.delete(el);
        return;
      }
      const rect = s.container.getBoundingClientRect();
      const raw = (rect.top + rect.height / 2 - window.innerHeight / 2) * s.speed;
      if (s.self) {
        s.target = raw;
      } else {
        // Keep the shift within the scale(1.15) slack so the container edge never shows
        const max = el.clientHeight * 0.07;
        s.target = Math.max(-max, Math.min(max, raw));
      }
    });
  }

  function lerp(a, b, t) {
    return a + (b - a) * t;
  }

  function tick() {
    state.forEach(function (s, el) {
      s.current = lerp(s.current, s.target, 0.07);
      if (Math.abs(s.current - s.target) < 0.05) s.current = s.target;
      el.style.transform = s.self
        ? 'translateY(' + s.current + 'px)'
        : 'scale(1.15) translateY(' + s.current + 'px)';
    });
    requestAnimationFrame(tick);
  }

  function start() {
    collect();
    onScroll();
    if (!running && state.size) {
      running = true;
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      requestAnimationFrame(tick);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  /* Re-run when sections are re-rendered in the theme editor */
  document.addEventListener('shopify:section:load', function () {
    start();
  });
})();
